import { FC, useState, useRef, KeyboardEvent, ReactChild } from "react";

import cx from "classnames";

import Icon, { IconTypes } from "./../Icon";
import List from "./List";
import { isLogicOption } from "./../../helpers/isLogicOption";

import styles from "./styles.module.scss";

export enum LogicSelectMode {
    DEFAULT,
    CHECKBOX,
    RADIO,
}
export interface ILogicOptions {
    [key: string]: {
        label: string;
        type: string;
    };
}

export interface IOtherLogicOptions {
    [key: string]: string;
}

export interface ICustoms {
    [key: string]: (
        onChange: (key: ILogicOptions["key"], index: number) => void,
        key: ILogicOptions["key"],
    ) => ReactChild;
}

interface IProps {
    options: ILogicOptions | IOtherLogicOptions;
    initial?: string | null;
    placeholder?: string;
    className?: string;
    disabled?: boolean;
    useListWithPortal?: boolean;
    valueKey?: string;
    handleChange: (newKey: string) => void;
}

const Select: FC<IProps> = ({
    options,
    initial,
    placeholder = "",
    className,
    disabled,
    valueKey = "",
    handleChange,
}) => {
    const initialIndex = initial ? Object.keys(options).indexOf(initial) : 0;

    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [hoverElementIndex, setHoverElementIndex] = useState<number>(initialIndex);

    const selectField = useRef<HTMLButtonElement>(null);
    const isPlaceholder = !valueKey || valueKey === "";
    const optionsKeys = options ? Object.keys(options) : "";

    const onToggle = () => {
        if (!disabled) setIsOpen(!isOpen);
    };

    const onChange = (newKey: string, index: number) => {
        if (!disabled) {
            handleChange(newKey);
            setHoverElementIndex(index);
            setIsOpen(false);
        }
    };

    const onKeyDown = (e: KeyboardEvent) => {
        e.preventDefault();
        if (!disabled) {
            switch (e.keyCode) {
                case 38:
                    if (hoverElementIndex > 0) setHoverElementIndex(hoverElementIndex - 1);
                    break;
                case 40:
                    if (hoverElementIndex < optionsKeys.length - 1) setHoverElementIndex(hoverElementIndex + 1);
                    break;
                case 13:
                    const newKey = optionsKeys[hoverElementIndex];
                    handleChange(newKey);

                    setIsOpen(false);
                    break;
            }
        }
    };
    return (
        <>
            <button
                ref={selectField}
                className={cx(styles.container, className, {
                    [styles.open]: isOpen,
                })}
                onFocus={() => {
                    if (!isOpen) setIsOpen(true);
                }}
                onKeyDown={onKeyDown}
                disabled={disabled}
            >
                <div
                    className={cx(styles.header, {
                        [styles.placeholder]: isPlaceholder,
                        [styles.disabled]: disabled,
                    })}
                    onMouseDown={onToggle}
                >
                    <span>
                        {options[valueKey]
                            ? isLogicOption(options[valueKey])
                                ? (options[valueKey] as { label: string; type: string }).label
                                    ? (options[valueKey] as { label: string; type: string }).label
                                    : placeholder
                                : options[valueKey]
                            : placeholder}
                    </span>

                    <Icon className={cx(styles.icon, { [styles.active]: isOpen })} type={IconTypes.DOWN} size={20} />
                </div>

                {isOpen && (
                    <List
                        onChange={onChange}
                        setIsOpen={setIsOpen}
                        options={options}
                        selectField={selectField}
                        currentKey={valueKey}
                        hoverElementIndex={hoverElementIndex}
                    />
                )}
            </button>
        </>
    );
};

export default Select;
